/**
 * 合作内容「表格模式」组件 —— demo 可评审版。
 *
 * 用 ProTableListForm 把「每条合作内容」渲染为一行：
 *  - L2 属性集：每属性一个 custom 列（standards + properties 依序横排），render(row) 拿整行做级联
 *  - L3/L4 指标/权益：subArray 列，列定义交给 kpiSubArraySpec/equitySubArraySpec（消费
 *    KPITable/kpiColumnRules 与 EquityFormTable/equityColumnRules 的逻辑层规则——一份规则、两处渲染）
 *  - L1 行级标量：need_ai(select) / remark(input)
 *  - 行操作：复制 / 删除 / 应用至整单；表级操作：新增一行
 *
 * 【Demo 标注】kpiList/rightsList/detailState 等运行参数暂用 mock 默认值；
 * 正式接入时由外层（详情页/拆单弹窗）注入真实 useKPIList/schema?.expect_rights_list 等。
 */
import React, { useMemo, useRef, useImperativeHandle, useState } from 'react';
import { Form, Button, Message } from '@bedrock/components';
import { Plus } from '@bedrock/icons-react';
import { useMemoizedFn } from 'ahooks';
import { ProTableListForm, type ProTableListFormHandle, type SubArraySpec, type ColumnMeta } from '@mpc/common/src/components/ProTableListForm';
import { $sType } from '#utils';
import { getCooperateAIRequired } from '#utils/CONST';
import useKPIList from '../KPITable/useKPIList';
import { buildKpiSubArraySpec } from './kpiSubArraySpec';
import { buildEquitySubArraySpec } from './equitySubArraySpecTable';
import useAddEquityModal from '../useAddEquityModal';
import type { CooperationTableListProps, CooperationTableListHandle } from './CooperationTableTypes';
import { buildCooperationColumns } from './buildCooperationColumns';
import { useRowMutations } from './useRowMutations';
import type { CoverMode } from './applyToWholeOrder';

// 行高由 subArray（kpi/necessary/expect 子项数）自动计算，见 ProTableListForm 的 sideHeight 逻辑。

// Props / Handle 类型已外移到 ./CooperationTableTypes（那 66 行几乎全是「与折叠面板某处对齐」
// 的跨模式契约注释，留在容器里会把装配与编排挤到 600 行之后）。此处 re-export 保持对外出口不变。
export type { CooperationTableListProps, CooperationTableListHandle };

// 校验错误红框样式**不再在此处声明**：引擎 ProTableListForm 已全局注入 ERR_STYLE
//（见 ./ProTableListForm/cellError.tsx，唯一来源）。此处曾有一份 scoped 副本，
// 且它是引擎版的**真子集** —— 少了 `.rock-picker`，导致日期 / 日期区间出错时没有红框。
// 删除副本即由引擎版接管，日期类控件的红框随之生效。

// ---- 表格高度：传给引擎 maxHeight（60% 屏高封顶），引擎内部按内容总高(含子项)自适应展开 ----
/** 表格最大高度比例：不超过屏幕高度的 60%（用户规则 1） */
const MAX_TABLE_HEIGHT_RATIO = 0.6;
/** 表格最小高度（下限）：单行且无子项时内容总高仅 ~40px，需抬升至容纳「表头 + 1 内容行 + 添加子项行」，
 *  避免仅一行合作内容且未添加指标时表格高度过扁（用户反馈）。 */
const MIN_TABLE_HEIGHT = 200;

// 本地兜底行 id 前缀（LOCAL_ROW_ID_PREFIX）与发布时间重算延迟
//（ADD_ROW_RELEASE_TIME_DELAY_MS）已随 id 归一化与写回逻辑移至 ./useRowMutations。

/**
 * 权益可「应用至整单」的单据状态（对齐折叠面板 hideOptions 的放行集合）。
 * 结构化版本 < 1 时，仅设计/进行中的单据允许把权益应用到整单。
 */
const RIGHTS_APPLY_STATES = ['pending_design', 'underway'];

/** 权益库列表兜底空值：稳定引用，避免每次渲染新建数组导致 useMemo 依赖抖动 */
const EMPTY_RIGHTS_LIST: { content?: string; code?: string }[] = [];

// 属性列宽助手（WIDE/NARROW_ATTR_COLUMN_WIDTH + getAttrColumnWidth）已移至 ./propertyColumns
// —— 列宽判据（span / controlType）与属性列构建同源，分开会出现两处漂移。

/**
 * 属性列必填判据 / 选项匹配 / 属性校验 与 `isEmptyFieldValue` 已外移到 ./propertyValidation
 *（那四条是 `#utils.getOptionsRule` 的镜像实现，属「校验语义层」，与列渲染分开）。
 */

// 日期值 <-> dayjs 归一化（toDayjsValue / fromDayjsValue）与控件分派渲染（renderPropertyControl）
// 已移至 ./propertyColumns —— 它们是属性列控件的一部分，唯一消费方也在那里。

// 「是否需包含AI」列（NEED_AI_TITLE / NEED_AI_OPTIONS / 值域转换 / NeedAiCell / 列构建）
// 已整块移至 ./needAiColumn —— 含「为何必须是 custom 列而非 doc 列」的两条推导（吞 boolean false /
// 批量弹层 !value 判空禁用「确定」），那两条必须与实现同处，否则会被后来者「顺手统一成 doc 列」。

// RowActions 与其列宽常量 ACTION_COLUMN_WIDTH 已移至 ./RowActions
//（列宽取值依据是实测该组件的内容宽，改了按钮/图标就该重算 → 两者必须同处）。

// 属性单元格组件 PropField 已移至 ./propertyColumns（与它的控件渲染 renderPropertyControl 同处）。

const CooperationTableList = React.forwardRef<CooperationTableListHandle, CooperationTableListProps>(function CooperationTableList(
  { value, onChange, schema, structuredVersion, view = false, addable = true, category_id, detailId, platforms = ['MPC'], moreKpi = false, groupType, detailState, colKeys, productArea, need_actual_execution, disableKpiCodeEdit, disableExpectValueEdit, kpiRequired, structured, hasKPI = false, disableAdd, disableDelete, structured_version, state, need_cooperate_account_supplier_settle_price, updateReleaseTime, pinnedCodes, togglePin }: CooperationTableListProps,
  ref: React.ForwardedRef<CooperationTableListHandle>,
) {
  const tableRef = useRef<ProTableListFormHandle>(null);
  const isInner = $sType === 'inner';

  // KPI 真实列表：对齐折叠模式 KPITable 的 useKPIList(!view || moreKpi, detailId)（模块级缓存接口数据）
  const kpiList = useKPIList(!view || moreKpi, detailId);
  // 权益真实列表：schema 后端已带 necessary/expect rights_list（对齐折叠面板：无则空，不做 mock 兜底）
  const necessaryRightsList = schema?.necessary_rights_list || EMPTY_RIGHTS_LIST;
  const expectRightsList = schema?.expect_rights_list || EMPTY_RIGHTS_LIST;
  const showNeedAi = !!structured && getCooperateAIRequired(category_id);
  /** 仅剩一行时禁止删除（对齐折叠面板：删除按钮 disabled={dataList.length <= 1}） */
  const isSingle = (value?.length ?? 0) <= 1;
  /**
   * 是否为「旧结构化版本」（< 1）。
   * ⚠️ 与面板 `detail?.structured_version < 1` 语义一致：版本号缺失时 JS 的 `undefined < 1`
   * 为 false（不视为旧版本），故此处也必须先判存在，不能用 `(x ?? 0) < 1`。
   */
  const isLegacyStructuredVersion = structured_version !== undefined && structured_version < 1;
  /**
   * 「字段应用至整单」弹层里不作为可应用字段的选项 —— 条件逐条对齐折叠面板
   * （CooperationFormList/index.tsx:491-501）：
   *  - kpi_values：无考核指标，或「需结算价」场景下该行不实际执行 → 不提供
   *  - necessary_rights / expect_rights：旧结构化版本且单据不在设计/进行中 → 不提供
   */
  const buildHideOptions = useMemoizedFn((row: Record<string, any>) => [
    {
      name: 'kpi_values',
      hide: !hasKPI || (!!need_cooperate_account_supplier_settle_price && row?.need_actual_execution === false),
    },
    {
      name: 'necessary_rights',
      hide: isLegacyStructuredVersion && !RIGHTS_APPLY_STATES.includes(state as string),
    },
    {
      name: 'expect_rights',
      hide: isLegacyStructuredVersion && !RIGHTS_APPLY_STATES.includes(state as string),
    },
  ]);

  /**
   * 校验合作内容（外部触发：提交拆单 / 底部「校验」按钮）。
   * 置全量校验态使全部必填空格标红，并由引擎滚动定位到第一个出错单元格。
   */
  const validateCooperate = useMemoizedFn(() => {
    setForceValidateAll(true);
    return tableRef.current?.validateAndLocate?.() ?? false;
  });

  /** 底部「校验合作内容」按钮：快捷校验，全部通过时给出成功反馈（有错时已自动滚动定位） */
  const handleValidateCooperate = useMemoizedFn(() => {
    if (!validateCooperate()) Message.success('校验通过');
  });

  // 暴露校验能力（桥接 ProTableListFormHandle）：供外层 CFormList 在 table 态校验整体值，
  // 以及底部「校验合作内容」按钮做快捷校验。
  useImperativeHandle(ref, () => ({
    validateFields: () => tableRef.current?.validateFields?.() ?? Promise.resolve(),
    validateAndLocate: validateCooperate,
  }));

  // ---- 权益子数组"添加" → 打开「关联权益」弹窗（对齐 EquityFormTable/index.tsx 的 openAddEquityModal）----
  // pending 记录当前触发弹窗的 ProTableListForm.onAddRequest 的 commit 回调，弹窗 onOk 时取出调用。
  const pendingEquityCommitRef = useRef<((items: any[]) => void) | null>(null);
  const openAddEquityModal = useAddEquityModal({
    rowKey: 'rights_code',
    onOk: (data: any[]) => {
      const items = (data || []).map((v) => ({ ...v, actual_content: v.content }));
      pendingEquityCommitRef.current?.(items);
      pendingEquityCommitRef.current = null;
    },
  });
  /**
   * 权益子数组 onAddRequest 的统一入口：inner 打开「关联权益」弹窗选标准权益；
   * 非 inner（外包）对齐 EquityFormTable 的 else 分支，直接追加一条空行。
   */
  const requestAddEquity = useMemoizedFn((subKey: 'necessary_rights' | 'expect_rights', row: Record<string, any>, commit: (items: any[]) => void) => {
    if ($sType !== 'inner') {
      commit([{ id: `${subKey}-${Date.now()}`, content: '', actual_content: '' }]);
      return;
    }
    pendingEquityCommitRef.current = commit;
    openAddEquityModal({
      disabledRows: row[subKey] || [],
      // 弹框权益库数据源：真实 schema 权益库（按当前子数组取必要/期望），非 mock
      rightsList: subKey === 'necessary_rights' ? necessaryRightsList : expectRightsList,
    });
  });

  // ---- 行 id 归一化 + 受控写回（全部外移到 ./useRowMutations） ----
  // ⚠️ tableListValue / handleTableChange 与所有写回回调是**同一机制的两半**（缺 id 的行补本地
  // 兜底 id → 写回前剥离），故整对一起搬走；回调必须基于 tableListValue 匹配行的约束也随之
  // 移入该文件。容器只消费，不再自持。
  const {
    tableListValue,
    handleTableChange,
    updateProperty,
    updateRow,
    batchApplyProperty,
    batchApplyRow,
    copyRow,
    deleteRow,
    applyRowToWholeOrder,
    applySubArrayToWholeOrder,
    addRow,
  } = useRowMutations({ value, onChange, schema, updateReleaseTime });

  // ---- 子数组「应用至整单」（三类区块共用同一条门控与同一种语义） ----
  /**
   * 是否显示子数组「应用至整单」—— 与卡片模式**逐字同一条门控**。
   *
   * `CooperationFormList` 的面板分支对 KPITable / EquityFormTable 传的就是
   * `!view && addable && detail?.structured`（其中 `detail?.structured` 在本组件即 `structured` prop）。
   * 对齐后：① 两形态表现一致；② 策划拆单预览态（`PlannerSplitPreview` 传 `addable={false}`）
   * 与卡片模式一样**不显示**；③ 查看态（`view`）不显示。
   *
   * ⚠️ 引擎侧还会再叠一层「与『+添加{emptyLabel}』同门控」（`subHasAddRow(sub)`，
   * 即 `disableAdd` 为真时不渲染）—— 两者是**与**关系，故拆单「已有执行周期」纬度同样不显示。
   */
  const canApplyToTheWholeOrder = !view && addable && !!structured;
  // 三个子数组各自的动作（intent 与 requestAddEquity 同：用 useMemoizedFn 保 identity 永久稳定，
  // 供 subArrays 的 useMemo 依赖消费 —— 见 dev.spec 陷阱 13）
  // `coverMode` 来自各区块弹层内的单选（保留原值 / 覆盖原值），此处仅透传给写回层。
  const applyKpiSubArray = useMemoizedFn((row: Record<string, any>, coverMode: CoverMode) => applySubArrayToWholeOrder(row.id, 'kpi_values', coverMode));
  const applyNecessaryRightsSubArray = useMemoizedFn((row: Record<string, any>, coverMode: CoverMode) => applySubArrayToWholeOrder(row.id, 'necessary_rights', coverMode));
  const applyExpectRightsSubArray = useMemoizedFn((row: Record<string, any>, coverMode: CoverMode) => applySubArrayToWholeOrder(row.id, 'expect_rights', coverMode));

  // 钉在左侧：pinnedCodes / togglePin 由父级 CooperationFormList 持有
  //（切「卡片」时本组件会卸载，内部 state 会丢，钉住随之消失 —— 见 CooperationTableTypes 注释 / #46364）
  /**
   * 「添加合作内容」按钮：新增一行后把新行滚入可视区。
   *
   * 表格高度按内容自适应但受 `maxHeight`（60% 屏高）封顶，封顶后新增行会落在视口外
   * 或只露出一截。故写回后主动滚到底，让用户直接看到并填写新行。
   *
   * ⚠️ 不要在这里再包 `requestAnimationFrame`：写回后布局要到下一帧才稳定，
   * 引擎的 `scrollToLastRow` 内部已按帧重滚到收敛（旧写法只滚一帧，会停在加行前的位置，
   * 现象为「新增行只露出半截」——走查 #31629 复报的那条）。
   */
  const handleAddRow = useMemoizedFn(() => {
    addRow();
    tableRef.current?.scrollToLastRow?.();
  });
  /**
   * 全量校验态：外部触发校验（提交拆单 / 底部「校验」按钮）后置 true。
   *
   * 红框此前是纯渲染期计算，任一格输入都会因整表重渲染而让全表必填空格一起标红（扩散）。
   * 现收敛为：输入期只对「用户实际编辑过的格」生效；只有外部全量校验后才全表标红。
   */
  const [forceValidateAll, setForceValidateAll] = useState(false);

  // ---- 子数组：指标 / 必要权益 / 期望权益（列定义交给 kpiSubArraySpec/equitySubArraySpec，
  //      消费 KPITable/kpiColumnRules 与 EquityFormTable/equityColumnRules 逻辑层规则） ----
  const needResultStates = ['process'];
  /**
   * KPI 行级隐藏**总开关，当前关闭**。
   *
   * 该规则对齐面板 `index.tsx:615` 的 `hide`，但经确认它服务的是**非拆单场景**，拆单不需要：
   * 拆单 `depth ∈ {0,1}` 时行级 `need_actual_execution` 为 `undefined`，`=== false` 本就不成立。
   *
   * ⚠️ 实测教训：后端返回的行若带 `need_actual_execution: false`，且单据
   * `need_cooperate_account_supplier_settle_price` 为 true，则**全行命中** → 整个考核指标列
   * （含「+ 添加指标」）消失，用户无处可加指标。故暂不启用。
   *
   * 引擎能力（`SubArraySpec.visibleRow`）与下方判定均已就绪，待非拆单场景接入表格模式时改 true。
   */
  const ENABLE_KPI_ROW_HIDE = false;
  /**
   * 该行 KPI 是否整组隐藏。对齐面板 `CooperationFormList/index.tsx:615` 的
   * `hide: !hasKPI || (need_cooperate_account_supplier_settle_price && dataList[index]?.need_actual_execution === false)`。
   *
   * ⚠️ `need_actual_execution` 直接从**行数据**取（面板即行级语义），与整表级的
   * `need_actual_execution` prop 无关 —— 后者语义是「整表只读」，本次不牵动。
   */
  const isKpiRowHidden = useMemoizedFn(
    (row: any) => !!need_cooperate_account_supplier_settle_price && row?.need_actual_execution === false,
  );
  /**
   * KPI 列级降级（胶水层）：**所有行**都不需要实际执行时，整个 KPI 列（含组标题）都不该出现。
   *
   * 拆成独立 useMemo 返回**布尔**，是为了让下方 subArrays 的依赖落在这个布尔上而非 `value` 数组：
   * `value` 每次子项编辑都会换引用，若直接进 subArrays 的依赖，列定义会被反复重建
   *（@bedrock table 据此重算列，开销远大于一次布尔比较）。
   */
  const kpiHiddenOnAllRows = useMemo(
    () => ENABLE_KPI_ROW_HIDE && !!need_cooperate_account_supplier_settle_price && value.length > 0 && value.every((r) => r?.need_actual_execution === false),
    [value, need_cooperate_account_supplier_settle_price],
  );
  const subArrays: SubArraySpec<any, any>[] = useMemo(() => {
    const out: SubArraySpec<any, any>[] = [];
    // 考核指标：无 KPI 的需求类型不渲染该列（对齐折叠面板 KPITable 面板的 hide: !hasKPI）；
    // 全行都不需实际执行时，整列降级隐藏（对齐面板同一条 hide 的行级判定在「全行命中」时的效果）。
    // ⚠️ 后半句拆单不命中（见 isKpiRowHidden 注释），拆单等价于原来的 `if (hasKPI)`。
    if (hasKPI && !kpiHiddenOnAllRows) {
      out.push(buildKpiSubArraySpec({
        kpiList,
        platforms,
        view,
        moreKpi,
        detailState,
        structuredVersion,
        groupType,
        colKeys,
        needResultStates,
        isInner,
        $sType,
        productArea,
        need_actual_execution,
        disableKpiCodeEdit,
        disableExpectValueEdit,
        // 行级隐藏：混合场景（部分行 need_actual_execution === false）由引擎逐行遮挡该组。
        // ⚠️ 受 ENABLE_KPI_ROW_HIDE 总开关控制（**当前关闭** → 恒为 undefined，即全部可见）
        visibleRow: ENABLE_KPI_ROW_HIDE ? isKpiRowHidden : undefined,
        // 隐藏「+添加指标」：拆单「已有执行周期」纬度（disableAdd），或查看态且非 moreKpi。
        // 对齐面板 KPITable/index.tsx:980 `need_actual_execution !== false && (!view || moreKpi) && !disableAdd`
        // （式中 `need_actual_execution !== false` 为行级条件，表格列模型暂无法表达，见文档 N6）。
        // 引擎除隐藏按钮外，还会据此同步扣减该行「添加行」高度（见 ProTableListForm 的 subHasAddRow）。
        disableAdd: disableAdd || (view && !moreKpi),
        disableDelete,
        // 考核指标整体必填（对齐面板模式 kpiTableProps.required → cooperateKpiRequired 判定）
        required: kpiRequired,
        // 「应用至整单」（对齐卡片模式 KPITable 的同名按钮）：以本行指标数组为源整单覆盖。
        // 引擎把它渲染在**第二子列**（拆单场景即「直接考核」列），显隐与「+添加指标」同门控。
        onApplyToTheWholeOrder: canApplyToTheWholeOrder ? applyKpiSubArray : undefined,
      }));
    }
    out.push(buildEquitySubArraySpec({
      subKey: 'necessary_rights',
      title: '必要权益',
      rightsList: necessaryRightsList,
      view,
      $sType,
      onRequestAdd: (row, commit) => requestAddEquity('necessary_rights', row, commit),
      // 「应用至整单」（对齐卡片模式 EquityFormTable 的同名按钮）：以本行必要权益数组为源整单覆盖
      onApplyToTheWholeOrder: canApplyToTheWholeOrder ? applyNecessaryRightsSubArray : undefined,
    }),
    buildEquitySubArraySpec({
      subKey: 'expect_rights',
      title: '期望权益',
      rightsList: expectRightsList,
      view,
      $sType,
      onRequestAdd: (row, commit) => requestAddEquity('expect_rights', row, commit),
      onApplyToTheWholeOrder: canApplyToTheWholeOrder ? applyExpectRightsSubArray : undefined,
    }));
    return out;
  }, [isInner, requestAddEquity, view, kpiList, platforms, moreKpi, detailState, structuredVersion, groupType, colKeys, productArea, need_actual_execution, disableKpiCodeEdit, disableExpectValueEdit, kpiRequired, necessaryRightsList, expectRightsList, hasKPI, disableAdd, disableDelete, kpiHiddenOnAllRows, isKpiRowHidden, canApplyToTheWholeOrder, applyKpiSubArray, applyNecessaryRightsSubArray, applyExpectRightsSubArray]);

  // ---- 列定义：行级标量 + 属性列(custom) + 指标/权益(subArray) + 备注 + 操作列 ----
  // 装配编排已外移到 ./buildCooperationColumns（纯函数）。
  // ⚠️ useMemo 与下面这行依赖数组**留在此处且逐字冻结**（门禁 scripts/check-deps-array.mjs）：
  //    漏 view/pinnedCodes → 查看态切换或钉住后列不更新；漏 forceValidateAll → 校验态不刷新。
  //    另有**有意省略**项 batchApplyProperty / batchApplyRow（useMemoizedFn，identity 稳定），勿「补全」。
  const columns: ColumnMeta<any, any>[] = useMemo(() => buildCooperationColumns({
    schema, subArrays, structuredVersion, updateProperty, updateRow, copyRow, deleteRow,
    applyRowToWholeOrder, showNeedAi, view, pinnedCodes, togglePin, forceValidateAll,
    buildHideOptions, isSingle, batchApplyProperty, batchApplyRow,
  }), [schema, subArrays, structuredVersion, updateProperty, updateRow, copyRow, deleteRow, applyRowToWholeOrder, showNeedAi, view, pinnedCodes, togglePin, forceValidateAll, buildHideOptions, isSingle]);

  // 行高由 subArray（kpi/necessary/expect 子项数）自动计算，见 ProTableListForm 的 sideHeight 逻辑。
  // 表格容器高度：传 maxHeight=60%屏，引擎按「内容总高(含子项)」自适应展开、封顶后滚动（用户规则）
  const viewportMax = useMemo(
    () => Math.floor((typeof window !== 'undefined' ? window.innerHeight : 888) * MAX_TABLE_HEIGHT_RATIO),
    [],
  );

  return (
    <Form component={false}>
      <ProTableListForm<any, any>
        ref={tableRef}
        value={tableListValue}
        onChange={handleTableChange}
        subArrays={subArrays}
        columns={columns}
        maxHeight={viewportMax}
        minHeight={MIN_TABLE_HEIGHT}
      />
      {/* ProTableListFormHandle.addRow 引擎内暂为空实现（见 ProTableListForm.tsx），
          表内追加一行改由本组件自持 addRow 直接改写 value 实现。
          pt-2 = 8px（tailwind.config getSpacing: key×4）上内边距；两按钮并排、间距 8px。 */}
      {!view && (
        <div className="flex items-center gap-2 pt-2">
          <Button size="small" type="checked" icon={<Plus />} onClick={handleAddRow}>添加合作内容</Button>
          {/* 快捷校验：有错时引擎内部自动滚动定位到第一个出错单元格 */}
          <Button size="small" type="checked" onClick={handleValidateCooperate}>校验</Button>
        </div>
      )}
    </Form>
  );
});

export default CooperationTableList;
